import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { UserAuth } from "../../utils/AuthContext";
import RoundedTable, {
  TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import PageWrapper from "../../components/PageWrapper";
import MultipleSearchFilter from "../../components/MultipleSearchFilter";
import InformationBox from "../../components/InformationBox";
import Loader from "../../components/Loader";

const PaperSellInvoices = () => {
  // ====================
  // 1. Context & Translations
  // ====================
  const { userData } = useContext(UserAuth);
  const { t } = useTranslation("general");

  // ====================
  // 2. State Management
  // ====================
  const [invoices, setInvoices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "id",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(false);

  // ====================
  // 3. Constants
  // ====================
  const titles = [
    { value: "invoiceNumber" },
    { value: "customerName" },
    { value: "Date" },
    { value: "amount" },
  ];

  const searchTitles = [
    { title: t("invoiceNumber"), value: "id", type: "text" },
    { title: t("customerName"), value: "customer", type: "text" },
    { title: t("finalAmount"), value: "finalAmount", type: "money" },
  ];

  // ====================
  // 4. API Functions
  // ====================
  const getColorSellInvoices = async (page = 1, limit = 5, searchTerm = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        organizationId: userData?.organizationId,
        categories: userData?.organizationType,
      };

      // Always include date parameters if they exist
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Add search parameters based on selected type
      if (selectedTitle.value !== "date") {
        params.searchQuery = searchTerm;
        params.searchType = selectedTitle.value;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getFabricSellInvoices`,
        {
          headers: {
            Authorization: `Bearer ${userData?.token}`,
          },
          params,
        }
      );

      if (response.status === 200) {
        setInvoices(response.data.data);
        setPagination({
          currentPage: response.data.pagination.currentPage,
          totalPages: response.data.pagination.totalPages,
          totalRecords: response.data.pagination.totalCount,
        });
      }
    } catch (error) {
      console.error("Error fetching color invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // ====================
  // 5. Handler Functions
  // ====================
  const handleSearchType = (type) => {
    setSelectedTitle(type);
  };

  const handleSearch = () => {
    // Validate date range if both dates are provided
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start date must be before end date");
      return;
    }
    getColorSellInvoices(1, 5, searchQuery);
  };

  // ====================
  // 6. Effects
  // ====================
  useEffect(() => {
    getColorSellInvoices(pagination.currentPage, 5, searchQuery);
  }, [pagination.currentPage]);

  // ====================
  // 7. Table Data Preparation
  // ====================
  const invoicesTable = {
    titles,
    rows: invoices.map((invoice) => [
      {
        value: invoice.id,
        type: TABLE_CELL_TYPES.LINK,
        to: `/paper-sell-invoice/${invoice.id}`,
      },
      { value: invoice.customer?.name || "-", type: TABLE_CELL_TYPES.TEXT },
      {
        value: new Date(invoice.date).toLocaleDateString(),
        type: TABLE_CELL_TYPES.TEXT,
      },

      {
        value: `${invoice.finalAmount} ${t(invoice?.currency?.toLowerCase())}`,
        type: TABLE_CELL_TYPES.TEXT,
      },
    ]),
  };

  // ====================
  // 8. Render
  // ====================
  return (
    <PageWrapper withBackButton>
      <div className="o-column o-column--gap-size_300 u-padding--bottom_500">
        <h1 className="t-headline-1">{t("invoicesList")}</h1>

        <div className="o-column o-column--gap-size_100 u-padding--bottom_500">
          {/* Search section */}
          <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end u-margin--bottom_400">
            <MultipleSearchFilter
              options={searchTitles}
              selected={selectedTitle}
              setSelected={handleSearchType}
              handleSearch={handleSearch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              flex
            />
          </section>
        </div>

        {/* Invoices Table */}
        {loading ? (
          <Loader />
        ) : (
          <>
            {invoicesTable.rows.length === 0 ? (
              <InformationBox>{t("noInvoiceFound")}</InformationBox>
            ) : (
              <RoundedTable
                titles={invoicesTable.titles}
                rows={invoicesTable.rows}
                pagination={pagination}
                setPagination={setPagination}
              />
            )}
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default PaperSellInvoices;
